import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

interface ScrollItem {
  chatRoomMessageId: string;
}

interface UseInfiniteScrollProps<T extends ScrollItem> {
  fetchNextPage: () => void;
  isFetching: boolean;
  hasNextPage: boolean;
  data: T[];
}

export const useInfiniteScroll = <T extends ScrollItem>({
  fetchNextPage,
  isFetching,
  hasNextPage,
  data
}: UseInfiniteScrollProps<T>) => {
  const firstItemIdBeforeLoadRef = useRef<string | null>(null);

  const loadMore = useCallback(() => {
    if (!isFetching && hasNextPage && data.length > 0) {
      firstItemIdBeforeLoadRef.current = data[0].chatRoomMessageId;
      fetchNextPage();
    }
  }, [isFetching, hasNextPage, fetchNextPage, data]);

  // 스크롤 이벤트를 감지하는 Effect
  useEffect(() => {
    const handleScroll = () => {
      // 페이지 최상단에 닿으면 loadMore 호출
      if (window.scrollY === 0) {
        loadMore();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadMore]);

  // 데이터 로드 후 스크롤 위치를 복원하는 Effect
  // biome-ignore lint/correctness/useExhaustiveDependencies: data 의존성 추가
  useLayoutEffect(() => {
    if (firstItemIdBeforeLoadRef.current) {
      const firstItemElement = document.getElementById(
        `message-${firstItemIdBeforeLoadRef.current}`
      );

      if (firstItemElement) {
        firstItemElement.scrollIntoView({ behavior: 'auto', block: 'start' });
      }

      firstItemIdBeforeLoadRef.current = null;
    }
  }, [data]);
};
